import CONFIG from "@APP/config";
import {
  AdminReusableRTPFilter,
  RequestPagination,
  ResponsePagination,
  ReusableRTP,
  ReusableRTPPayment,
  RTP,
  RTPQueryFilter,
} from "@APP/types";

import request from "../request";

const REUSABLE_RTP_URL = `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/request-to-pay/reusable-rtps`;

/**
 * Fetch a page of reusable RTP templates within a date range, optionally
 * filtered by `username` (exact match) and/or `orgId` (one or many).
 */
export const getReusableRTPs = async (
  filter: AdminReusableRTPFilter,
  pagination?: RequestPagination,
) => {
  const { page = 0, entries = 25 } = pagination ?? {};
  const { orgId, ...restFilter } = filter;
  const orgIds = Array.isArray(orgId) ? orgId : orgId ? [orgId] : [];
  // Repeated-key form (orgId=a&orgId=b), matching getPaymentsFromUserOrg's convention for
  // this same admin/request-to-pay/* endpoint family — not axios's default orgId[]=a&orgId[]=b.
  const orgIdQuery = orgIds.map((id) => `orgId=${encodeURIComponent(id)}`).join("&");

  const response = await request<{
    data: ReusableRTP[];
    links: ResponsePagination;
    meta: { totalItems: number };
  }>({
    method: "GET",
    url: orgIdQuery ? `${REUSABLE_RTP_URL}?${orgIdQuery}` : REUSABLE_RTP_URL,
    params: {
      page: `${page * entries};;;${entries}`,
      ...restFilter,
    },
  });

  return response.data;
};

export type ReusableRTPPaymentSort = {
  sort_by?:
    | "customer_name"
    | "payment_received_date"
    | "base_amount"
    | "tip_amount"
    | "total_amount_paid";
  sort_direction?: "asc" | "desc";
};

/** Fetch a page of payments made against a single reusable RTP, sorted server-side. */
export const getReusableRTPPayments = async (
  id: string,
  pagination?: RequestPagination,
  sort?: ReusableRTPPaymentSort,
) => {
  const { page = 0, entries = 10 } = pagination ?? {};

  const response = await request<{
    data: ReusableRTPPayment[];
    links: ResponsePagination;
    meta: { totalItems: number };
  }>({
    method: "GET",
    url: `${REUSABLE_RTP_URL}/${id}/payments`,
    params: {
      page: `${page * entries};;;${entries}`,
      ...(sort?.sort_by && { sort_by: sort.sort_by }),
      ...(sort?.sort_direction && { sort_direction: sort.sort_direction }),
    },
  });

  return response.data;
};

export const getPaymentRequests = (pagination?: RequestPagination, filter?: RTPQueryFilter) => {
  const { page, entries } = { page: 0, entries: 50, ...pagination };

  return request<{ data: RTP[]; links: ResponsePagination }>({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/request-to-pay/payment-requests`,
    params: {
      page: `${page * entries};;;${entries}`,
      ...filter,
    },
  });
};

export const getPaymentRequest = async (requestId: string) => {
  const response = await request<{ data: RTP[] }>({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/request-to-pay/payment-requests`,
    params: { requestId },
  });

  return response.data.data[0];
};

export const getPayments = async (orgId: string) => {
  const response = await request({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/request-to-pay/payment-requests`,
    params: {
      orgId,
    },
  });

  return response;
};

export const resendPaymentRequest = async (requestId: string) => {
  const response = await request<string>({
    method: "POST",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/request-to-pay/send/${requestId}`,
  });

  return response.data;
};

export const updateRTPStatusToPaid = (requestId: string) => {
  return request({
    method: "PUT",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/request-to-pay/${requestId}/paid`,
  });
};

export const getPaymentsFromUserName = async (from: string, username: string) => {
  const response = await request({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/request-to-pay/payment-requests`,
    params: {
      from,
      username,
    },
  });

  return response.data.data;
};

export const getPaymentsFromUserOrg = async (from: string, orgId: string[]) => {
  const baseUrl = `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/request-to-pay/payment-requests`;

  const orgIdParams = orgId.map((id) => `orgId=${encodeURIComponent(id)}`).join("&");
  const url = `${baseUrl}?from=${encodeURIComponent(from)}&${orgIdParams}`;

  const response = await request({
    method: "GET",
    url: url,
  });

  return response.data.data;
};

export const getAdminPaymentRequest = async (requestId: string) => {
  const response = await request<{ data: RTP[] }>({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/request-to-pay/payment-requests`,
    params: { requestId },
  });

  return response.data.data[0];
};
